import { useState } from "react";
import { Icon } from "@iconify/react";
import { AppModal, Input } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { requestPatientAccessApi } from "@/portals/caregiver/api/caregiverApi";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { RequiredMark } from "@/shared/components/RequiredMark";

interface RequestPatientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the request is actually sent, so the caller can refetch. */
  onRequested: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Deliberately has no live "is this a real account" pre-check — an inline
 * verified/name confirmation as you type would let a caregiver enumerate
 * which emails have MedSyncIQ accounts, which is a data-leakage risk. Submit
 * and see: a real, verified patient account gets a request; anything else
 * gets the same generic failure, so the response itself never confirms or
 * denies account existence either (see FamilyService.request_patient_access).
 */
export function RequestPatientModal({ open, onOpenChange, onRequested }: RequestPatientModalProps) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => setEmail("");

  const close = () => {
    onOpenChange(false);
    resetForm();
  };

  const canSubmit = EMAIL_PATTERN.test(email.trim()) && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const trimmed = email.trim();
    setIsSubmitting(true);
    const { error, code } = await requestPatientAccessApi(trimmed);
    setIsSubmitting(false);

    if (error) {
      const response = `${code ?? ""} ${error}`.toLowerCase();
      if (code === "PATIENT_ALREADY_YOUR_PATIENT") {
        toastError("This patient revoked your access. Only they can grant it back to you.");
      } else if (response.includes("active") || response.includes("approved") || response.includes("connected")) {
        toastError("This person is already your patient.");
      } else if (response.includes("pending") || response.includes("request_pending")) {
        toastError("You already have a pending request for this patient.");
      } else if (response.includes("already") || response.includes("exist") || response.includes("relationship")) {
        toastError("This person is already your patient.");
      } else {
        toastError(error);
      }
      return;
    }

    onRequested();
    toastSuccess(`Request sent to ${trimmed}`);
    close();
  };

  return (
    <AppModal open={open} onOpenChange={close} width={480} className="rounded-3xl p-7">
      <div className="mb-5 flex items-start gap-4 pr-6">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
          <Icon icon="lucide:plus" className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-extrabold text-[#0A2947]">Request Access to a Patient</h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Enter the patient's email. They'll see your request on their dashboard and decide whether to approve it.
          </p>
        </div>
      </div>

      <label className="mb-1.5 block text-xs font-semibold text-[#374151]">Email<RequiredMark /></label>
      <div className="msq-field-shell flex h-[46px] items-center gap-2.5 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-3 [&_:focus-visible]:!outline-none">
        <Icon icon="lucide:mail" className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
        <span className="h-4 w-px shrink-0 bg-[#E5E7EB]" />
        <Input
          variant="borderless"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@example.com"
          className="!h-full !w-full !p-0 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF]"
        />
      </div>

      <div className="mt-4 rounded-xl bg-[#FFF0F3] p-3 text-xs leading-relaxed text-[#374151]">
        They'll see your request on their dashboard and must approve it before you can view anything. Nothing is shared
        automatically.
      </div>

      <div className="mt-6 flex justify-center gap-3">
        <Button
          variant="outline"
          onClick={close}
          disabled={isSubmitting}
          className="!h-auto !rounded-lg !border-[#E4002B]/50 !px-6 !py-2.5 !text-sm !font-semibold !text-[#E4002B] hover:!border-[#E4002B]/50 hover:!bg-[#FFF2F4] hover:!text-[#E4002B]"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={isSubmitting}
          className="!h-auto !rounded-lg !bg-[#E4002B]/50 !px-6 !py-2.5 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60"
        >
          Send Request
        </Button>
      </div>
    </AppModal>
  );
}
